import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { addClassProfile, fetchCalendarClassWithProfiles } from '../database/databaseClassProfile';
import { fetchClasses } from '../database/databaseClass';
import { notifyReservation, notifyTrial } from '../database/databaseNotifications';
import { hasEverHadBonus, hasPendingTrial, createTrialBonus } from '../database/databaseBonus';

const toYYYYMMDD = (d) => new Date(d).toISOString().split('T')[0];

const formatDateChip = (dateStr) => {
    const date = new Date(`${dateStr}T00:00:00`);
    return date.toLocaleDateString('es-ES', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
    });
};

const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

const Reserve = () => {
    const [user, setUser] = useState(null);

    const [classesList, setClassesList] = useState([]);
    const [loadingClasses, setLoadingClasses] = useState(true);
    const [classSelected, setClassSelected] = useState(null);
    const [settings, setSettings] = useState(null);

    const [expandedIndex, setExpandedIndex] = useState(null);
    const [selectedClassId, setSelectedClassId] = useState(null);

    const [filteredDates, setFilteredDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [availableHours, setAvailableHours] = useState([]);

    const [showTrialModal, setShowTrialModal] = useState(false);
    const [trialCalendarClassId, setTrialCalendarClassId] = useState(null);
    const [trialSettings, setTrialSettings] = useState({ url: '', bonusTypeId: null, price: null });

    const navigate = useNavigate();

    /* ---------------------- auth ---------------------- */
    useEffect(() => {
        supabase.auth.getUser().then(({ data }) => setUser(data.user ?? null));
    }, []);

    /* ---------------------- fetch classes + settings ---------------------- */
    useEffect(() => {
        (async () => {
            setLoadingClasses(true);
            const data = await fetchClasses();
            setClassesList(data || []);
            setLoadingClasses(false);

            const { data: settingsData, error: settingsError } = await supabase.from('settings').select('*');
            if (settingsError) {
                console.error('Error fetching settings:', settingsError.message);
            } else {
                setSettings(settingsData);

                const s = settingsData?.[0];
                if (s?.trial_bonus_type_id) {
                    const { data: bt } = await supabase
                        .from('bonustype')
                        .select('price')
                        .eq('id', s.trial_bonus_type_id)
                        .single();
                    setTrialSettings({
                        url: s.trial_class_payment_url ?? '',
                        bonusTypeId: s.trial_bonus_type_id,
                        price: bt?.price ?? null,
                    });
                } else {
                    setTrialSettings({ url: s?.trial_class_payment_url ?? '', bonusTypeId: null, price: null });
                }
            }
        })();
    }, []);

    /* ---------------------- click a class card ---------------------- */
    const onImageClick = async (item) => {
        const newExpanded = item.id === expandedIndex ? null : item.id;
        setExpandedIndex(newExpanded);
        setSelectedClassId(item.id);
        setSelectedDate(null);
        setAvailableHours([]);

        if (!newExpanded) {
            setFilteredDates([]);
            return;
        }

        const today = toYYYYMMDD(new Date());
        const daysAhead = toYYYYMMDD(new Date(Date.now() + (settings?.[0]?.reserve_days_ahead ?? 14) * 24 * 60 * 60 * 1000));

        const { data, error } = await supabase.from('calendarclass').select('date').eq('class', item.id).gte('date', today).lte('date', daysAhead).order('date', { ascending: true });

        if (error) {
            console.error('Error loading dates:', error.message);
            setFilteredDates([]);
            return;
        }

        const uniqueDates = Array.from(new Set((data || []).map((r) => toYYYYMMDD(r.date)))).map((d) => ({ value: d, label: formatDateChip(d) }));

        setFilteredDates(uniqueDates);
    };

    /* ---------------------- reserve action ---------------------- */
    const reserveClass = (calendarclassId) => async () => {
        if (!user) {
            navigate('/login', { state: { returnTo: '/reserve' } });
            return;
        }

        const selectedSlot = availableHours.find((s) => s.id === calendarclassId);

        let message = '¿Confirmar reserva de la clase?';
        if (selectedSlot?.isWaitlist) {
            message = `La clase está completa.\nSi confirmas, te añadiremos a la lista de espera, una notificación será enviada cuando haya un lugar disponible.`;
        }

        const confirm = window.confirm(message);
        if (!confirm) return;

        const bonusSessions = await supabase
            .from('bonus_classes')
            .select('*')
            .eq('user_id', user.id)
            .lte('from_date', selectedDate)
            .gte('to_date', selectedDate)
            .limit(1)
            .then(({ data, error }) => {
                if (error) {
                    console.error('Error checking bonus:', error.message);
                    return false;
                }
                if (data.length === 0) return null;
                return { bonusId: data[0].id, availableSessions: data[0].quantityofclasses - data[0].reserved_classes };
            });

        if (bonusSessions && bonusSessions.availableSessions <= 0) {
            alert('No tienes bonos con sesiones disponibles. Por favor, adquiere un bono antes de reservar una clase.');
            return;
        } else if (!bonusSessions) {
            if (await hasPendingTrial(user.id)) {
                alert('Ya tenés una solicitud de clase de prueba pendiente de aprobación. Contactanos por WhatsApp para más información.');
                return;
            }
            if (await hasEverHadBonus(user.id)) {
                alert('No tienes bonos con sesiones disponibles. Por favor, adquiere un bono antes de reservar una clase.');
                return;
            }
            setTrialCalendarClassId(calendarclassId);
            setShowTrialModal(true);
            return;
        }

        await doReservation(user.id, calendarclassId, selectedSlot?.isWaitlist, bonusSessions?.bonusId);
    };

    const doReservation = async (userId, calendarclassId, isWaitlist, bonusId, isTrial = false) => {
        await addClassProfile(userId, calendarclassId, isWaitlist, bonusId);

        const slot = availableHours.find((s) => s.id === calendarclassId);
        if (!isTrial) {
            notifyReservation(user.email, classesList.find((c) => c.id === selectedClassId)?.title, `${selectedDate} ${slot?.start}`);
        }

        setSelectedDate(null);
        setAvailableHours([]);
        setExpandedIndex(null);
        setFilteredDates([]);
        navigate('/profile', { state: { message: '¡Clase reservada exitosamente!' } });
    };

    /* ---------------------- confirm trial reservation ---------------------- */
    const confirmTrialReservation = async () => {
        if (!trialSettings.bonusTypeId) {
            alert('No hay un tipo de bono de prueba configurado. Contactá al administrador.');
            return;
        }

        const { data: trialBonus, error } = await createTrialBonus(
            user.id,
            trialSettings.bonusTypeId,
            trialSettings.price,
            selectedDate,
        );

        if (error) {
            alert(`Error al crear la reserva de prueba: ${error.message}`);
            return;
        }

        const selectedSlot = availableHours.find((s) => s.id === trialCalendarClassId);
        const className = classesList.find((c) => c.id === selectedClassId)?.title;
        notifyTrial('request', user.email, className, `${selectedDate} ${selectedSlot?.start}`);
        setShowTrialModal(false);
        await doReservation(user.id, trialCalendarClassId, selectedSlot?.isWaitlist, trialBonus.id, true);
    };

    const handleDateSelect = async (dateStr) => {
        setSelectedDate(dateStr);

        const data = await fetchCalendarClassWithProfiles(selectedClassId, dateStr);
        const slots = (data || []).map((row) => {
            const isWaitlist = row.confirmed_count >= row.maxattendees;
            return {
                id: row.calendarclass_id,
                start: hhmm(row.starttime),
                end: hhmm(row.endtime),
                comment: row.comment,
                maxattendees: row.maxattendees,
                confirmedCount: row.confirmed_count,
                participants: row.participants || '',
                isWaitlist,
            };
        });

        setAvailableHours(slots);
    };

    return (
        <Layout page='reserve'>
            <section className='min-h-screen bg-cream px-6 py-10 mt-12'>
                {loadingClasses ? (
                    <p className='text-center text-ink/50'>Cargando clases…</p>
                ) : classesList.length === 0 ? (
                    <p className='text-center text-ink/50'>No hay clases configuradas.</p>
                ) : (
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                        {classesList.map((item) => {
                            const isExpanded = expandedIndex === item.id;
                            const imgSrc = item.image ? item.image : '/images/placeholder.jpg';

                            return (
                                <div key={item.id} className={`p-6 rounded-xl shadow-lg transition-all duration-300 bg-card text-ink border ${isExpanded ? 'border-terracotta ring-1 ring-terracotta/30' : 'border-sand'}`}>
                                    <div onClick={() => onImageClick(item)} className='cursor-pointer'>
                                        <img src={imgSrc} alt={item.title} className='w-full h-48 object-cover rounded-md mb-4' />
                                        <h3 className='text-xl font-sans font-light uppercase tracking-wide text-ink mb-2'>{item.title}</h3>
                                        {item.schedule && (
                                            <div className='flex flex-wrap gap-1 mb-2'>
                                                {item.schedule.split('\n').filter(Boolean).map((line, i) => (
                                                    <span key={i} className='inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium bg-sand text-terracotta-dark'>
                                                        <svg xmlns="http://www.w3.org/2000/svg" className='w-3 h-3' fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
                                                        </svg>
                                                        {line.trim()}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {item.duration && <p className='text-sm'>Duración: {item.duration}</p>}
                                        {item.price && <p className='text-sm'>Precio: {item.price}</p>}
                                    </div>

                                    {isExpanded && (
                                        <div className='mt-4 text-sm border-t border-sand pt-4'>
                                            {item.description && <p className='mb-3'>{item.description}</p>}

                                            {filteredDates.length > 0 ? (
                                                <>
                                                    <p className='font-semibold mb-2'>Selecciona una fecha:</p>
                                                    <div className='flex gap-2 overflow-x-auto pb-2 mb-4'>
                                                        {filteredDates.map((d) => (
                                                            <button
                                                                key={d.value}
                                                                onClick={() => handleDateSelect(d.value)}
                                                                className={`px-4 py-1 rounded-full border transition ${selectedDate === d.value ? 'bg-terracotta border-terracotta text-cream' : 'border-terracotta/40 text-ink hover:bg-sand/60'}`}
                                                            >
                                                                {d.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </>
                                            ) : (
                                                <p className='text-ink/60 mb-4'>No hay fechas disponibles</p>
                                            )}

                                            {selectedDate && (
                                                <>
                                                    <p className='font-semibold mb-2'>Selecciona un horario:</p>
                                                    <div className='flex flex-wrap gap-2'>
                                                        {availableHours.length === 0 ? (
                                                            <span className='opacity-70'>No hay horarios para esa fecha.</span>
                                                        ) : (
                                                            availableHours.map((slot) => (
                                                                <button
                                                                    key={slot.id}
                                                                    onClick={() => setClassSelected(slot.id)}
                                                                    title={slot.participants ? `Participantes:\n${slot.participants}` : 'Sin participantes aún'}
                                                                    className={`px-4 py-3 rounded-lg border font-medium transition flex flex-col items-start gap-1
                                                                        ${classSelected === slot.id ? 'bg-terracotta border-terracotta text-cream' : 'border-sand hover:bg-sand/60'}
                                                                    `}
                                                                >
                                                                    <span className='text-sm font-semibold'>
                                                                        {slot.start} – {slot.end}
                                                                        <span className='ml-2 text-xs opacity-80'>
                                                                            ({slot.confirmedCount}/{slot.maxattendees})
                                                                        </span>
                                                                    </span>
                                                                    {slot.isWaitlist && <span className='text-[11px] px-2 py-0.5 rounded-full bg-sand text-terracotta-dark'>Lista de espera</span>}
                                                                </button>
                                                            ))
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                            {classSelected && (
                                                <div className='mt-4'>
                                                    {(() => {
                                                        const slot = availableHours.find((hour) => hour.id === classSelected);
                                                        if (!slot) return null;

                                                        return (
                                                            <>
                                                                {slot.comment && <p className='mb-2 italic text-sm bg-sand/60 p-2 rounded'>{slot.comment}</p>}

                                                                {slot.participants && (
                                                                    <div className='mb-3 bg-sand/40 p-3 rounded-lg text-xs'>
                                                                        <p className='font-semibold mb-1'>Participantes:</p>
                                                                        <ul className='list-disc list-inside space-y-1'>
                                                                            {slot.participants.split(', ').map((name, index) => (
                                                                                <li key={index}>{name}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}

                                                                <div className='mb-2 text-xs space-y-1'>
                                                                    {slot.isWaitlist && (
                                                                        <p>
                                                                            Esta clase está completa. Tu reserva será en <span className='underline'>lista de espera</span>
                                                                        </p>
                                                                    )}
                                                                </div>

                                                                <button onClick={reserveClass(classSelected)} className='mt-2 w-full bg-terracotta text-cream font-semibold py-2 px-4 rounded-full hover:bg-terracotta-dark transition'>
                                                                    Confirmar Reserva
                                                                </button>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* Modal clase de prueba */}
            {showTrialModal && (
                <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
                    <div className='bg-card border border-sand rounded-xl shadow-xl w-full max-w-md p-6'>
                        <h3 className='text-xl font-serif font-medium text-terracotta mb-2'>¡Tu primera clase de prueba!</h3>
                        <p className='text-ink/70 mb-4'>
                            ¡Bienvenido/a! Hemos notado que es tu primera vez. Para reservar tu plaza, primero abonas la clase por WhatsApp y luego confirmas la reserva.
                        </p>
                        {trialSettings.url && (
                            <a
                                href={trialSettings.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='block w-full text-center mb-3 bg-sand text-terracotta-dark font-semibold py-2 px-4 rounded-full hover:bg-terracotta-light hover:text-cream transition'
                            >
                                Pagar clase de prueba
                            </a>
                        )}
                        <div className='flex gap-3'>
                            <button
                                onClick={confirmTrialReservation}
                                className='flex-1 bg-terracotta text-cream font-semibold py-2 px-4 rounded-full hover:bg-terracotta-dark transition'
                            >
                                Confirmar reserva
                            </button>
                            <button
                                onClick={() => setShowTrialModal(false)}
                                className='flex-1 border border-sand text-ink py-2 px-4 rounded-full hover:bg-sand/60 transition'
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Reserve;
